import { useRef, useState } from 'react'
import TextField from 'components/common/TextField'
import styled from 'styled-components'
import ButtonBase from 'components/common/ButtonBase'
import type { Coupon } from 'shared-types/Coupon'
import { getTranslatedTextByKey } from 'utils/utils'
import { useStores } from 'hooks/useStores'
import type CouponError from 'mobx/Coupons/errors'
import { sendCustomEvent } from 'utils/analytics/analytics'
import { isExternal } from 'mobx/Coupons/utils'

const Container = styled.div`
	display: flex;
	gap: 0.5rem;
	align-items: start;
	padding-bottom: 1.25rem;
`

type StyleMode = 'default' | 'active' | 'filled' | 'error' | 'disabled'

const Input = styled(TextField)<{ state: StyleMode }>`
	.MuiInputBase-root {
		border-radius: var(--borderRadiusBtn);
		height: 40px;

		@media (max-width: 576px) {
			height: 35px;
		}
	}

	.MuiOutlinedInput-root {
		padding: 0.5rem 0.75rem;

		${({ state }) => {
			const style = (border: string, text = 'fonts', background = 'clear'): string =>
				`border-color: var(--${border});
				background-color: var(--${background});
				color: var(--${text});`

			switch (state) {
				case 'active':
					return style('strokeMenuTitle')
				case 'filled':
					return style('inactive')
				case 'error':
					return style('error')
				case 'disabled':
					return style('disable', 'inactive', 'optionalBackground')
				case 'default':
				default:
					return style('inactive', 'disabled')
			}
		}}
	}
`

const Button = styled(ButtonBase).attrs({ fullWidth: false })`
	padding: 0.5em 1em;
	min-width: unset;

	&.MuiButton-outlined {
		border-color: var(--inactive);
	}

	white-space: nowrap;
	-webkit-transition: border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
	-ms-transition: border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
	transition: border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
`

const MAX_INPUT_LENGTH = 50

const isCouponLengthValid = (input: string) => input.length <= MAX_INPUT_LENGTH

interface Props {
	locale: string
}

const AddManualCoupon = ({ locale }: Props) => {
	const [couponInput, setCouponInput] = useState<string>('')
	const { couponsStore } = useStores()
	const [loading, setLoading] = useState<boolean>(false)
	const [errorMessage, setErrorMessage] = useState<string>('')
	const [inputStyleMode, setInputStyleMode] = useState<StyleMode>('default')
	const inputRef = useRef<HTMLInputElement | null>(null)

	const setError = (msg: string) => {
		setLoading(false)
		setErrorMessage(msg)
	}

	const handleAddCoupon = async () => {
		setLoading(true)

		const coupon: Coupon | null = await couponsStore.getCoupon(couponInput).catch((err: CouponError) => {
			setError(err.text())
			setInputStyleMode('error')

			return null
		})

		console.log('about to do modal', coupon)

		setLoading(false)

		if (!coupon) {
			return
		}

		setCouponInput('')
		// TODO: Add better flow for guest users when adding a coupon
		coupon.flags.requireLogin = { value: true }

		couponsStore.setCouponModal(coupon)
		const external = isExternal(coupon)

		sendCustomEvent({
			category: 'add_coupon',
			action: 'clicked',
			coupon_code: external ? 'external' : coupon.code,
			coupon_name: coupon.title[locale],
			price: coupon.price,
			order_type: [...coupon.orderTypes],
			offer_type: external ? 'external' : coupon.offerType,
			page_path: window.location.pathname,
		})
	}

	return (
		<Container>
			<Input
				inputProps={{ 'data-testid': 'add-manual-coupon-input' }}
				placeholder={getTranslatedTextByKey('eCommerce.coupons.manual.placeholder', 'Type in a coupon code to add to wallet')}
				value={couponInput}
				state={inputStyleMode}
				onBlur={() => setInputStyleMode(couponInput ? 'filled' : 'default')}
				onFocus={() => setInputStyleMode(couponInput ? 'filled' : 'active')}
				ref={inputRef}
				setValueFunction={() => {
					setTimeout(() => inputRef.current?.focus())
				}}
				onChange={({ target: { value } }) => {
					if (isCouponLengthValid(value)) {
						setErrorMessage('')
						setCouponInput(value)
					}
				}}
				onKeyDown={async ({ key }) => {
					if (key === 'Enter') {
						await handleAddCoupon()
					}
				}}
				loading={loading}
				error={!!errorMessage}
				errorMessage={errorMessage}
			/>
			<Button data-testid="add-to-wallet-button" disabled={!couponInput} $type="optional" onClick={handleAddCoupon}>
				{getTranslatedTextByKey('eCommerce.coupons.manual.add', 'Add to wallet')}
			</Button>
		</Container>
	)
}

export default AddManualCoupon
